import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { heroSlides } from "@/mocks/hero";
import { getFilmById, getCategoryLabel } from "@/lib/films";

export default function HeroCarousel() {
  const [active, setActive] = useState(0);
  const total = heroSlides.length;

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActive((prev) => (prev + 1) % total);
    }, 6500);
    return () => window.clearInterval(timer);
  }, [total]);

  const move = (step: number) => {
    setActive((prev) => (prev + step + total) % total);
  };

  const activeSlide = heroSlides[active];
  const activeFilm = getFilmById(activeSlide.filmId);

  return (
    <section className="relative h-[460px] w-full overflow-hidden bg-primary-950 md:h-[600px]">
      {heroSlides.map((slide, index) => (
        <div
          key={slide.filmId}
          className={`absolute inset-0 transition-opacity duration-1000 ${index === active ? "opacity-100" : "opacity-0"}`}
          aria-hidden={index !== active}
        >
          <img
            src={slide.backdrop}
            alt=""
            className={`h-full w-full object-cover object-top ${index === active ? "animate-slow-zoom" : ""}`}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950/90 via-primary-950/50 to-primary-950/35" />
        </div>
      ))}

      <div className="relative mx-auto flex h-full w-full max-w-6xl flex-col items-center justify-center px-4 text-center md:px-6">
        {activeFilm && (
          <div key={activeFilm.id} className="animate-fade-up">
            <span className="inline-flex items-center gap-2 rounded-full bg-accent-500/90 px-3 py-1 text-xs font-medium text-background-50">
              <i className="ri-fire-line text-sm" />
              {activeSlide.kicker}
            </span>
            <h1 className="mt-5 font-heading text-4xl font-extrabold leading-tight text-background-50 md:text-6xl">
              {activeFilm.title}
            </h1>
            <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-background-100/90 md:text-base">
              {activeFilm.oneLine}
            </p>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-xs text-background-100/80 md:text-sm">
              <span>{getCategoryLabel(activeFilm.category)}</span>
              <span className="text-background-100/40">·</span>
              <span>{activeFilm.year}</span>
              <span className="text-background-100/40">·</span>
              <span>{activeFilm.director} 执导</span>
              <span className="text-background-100/40">·</span>
              <span>{activeFilm.cast.slice(0, 2).join(" / ")} 主演</span>
            </div>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link
                to={`/detail/${activeFilm.id}`}
                className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600 sm:w-auto"
              >
                <i className="ri-book-open-line text-base" />
                查看剧情介绍
              </Link>
              <Link
                to={`/category/${activeFilm.category}`}
                className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10 sm:w-auto"
              >
                <i className="ri-layout-grid-line text-base" />
                浏览同分类
              </Link>
            </div>
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={() => move(-1)}
        aria-label="上一张"
        className="absolute left-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur transition-colors hover:bg-background-50/35 md:left-6 md:h-11 md:w-11"
      >
        <i className="ri-arrow-left-s-line text-xl md:text-2xl" />
      </button>
      <button
        type="button"
        onClick={() => move(1)}
        aria-label="下一张"
        className="absolute right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur transition-colors hover:bg-background-50/35 md:right-6 md:h-11 md:w-11"
      >
        <i className="ri-arrow-right-s-line text-xl md:text-2xl" />
      </button>

      <div className="absolute bottom-5 left-1/2 flex -translate-x-1/2 items-center gap-2">
        {heroSlides.map((slide, index) => (
          <button
            key={slide.filmId}
            type="button"
            onClick={() => setActive(index)}
            aria-label={`切换到第 ${index + 1} 张`}
            className={`h-1.5 rounded-full transition-all ${index === active ? "w-6 bg-accent-500" : "w-2.5 bg-background-50/50"}`}
          />
        ))}
      </div>
    </section>
  );
}